import React, { memo } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { hasValue } from 'swipeclock-frontend';

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'color',
})(({ theme, color }) => ({
  backgroundColor: color?.dark || theme.palette.success.dark,
  color: theme.palette.common.white,
  flexShrink: 0,
  fontSize: theme.typography.h5.fontSize,
  fontWeight: 500,
  alignSelf: 'center',
  height: 64,
  width: 256,
  margin: `${theme.spacing(2)} 0 ${theme.spacing(2)}`,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
  transition: theme.transitions.create(
    ['background-color', 'transform', 'box-shadow'],
    { duration: 250 }
  ),
  '&:hover': {
    backgroundColor: alpha(color?.dark || theme.palette.success.dark, 0.9),
    boxShadow: theme.shadows[5],
  },
  '&:active': {
    transform: 'translateY(0)',
    boxShadow: theme.shadows[2],
  },
}));

function ActionButton({ children, to, onClick, color, id }) {
  const buttonProps = hasValue(to)
    ? { component: Link, to }
    : { type: 'button', onClick };

  return (
    <StyledButton id={id} color={color} {...buttonProps}>
      {children}
    </StyledButton>
  );
}

ActionButton.propTypes = {
  children: PropTypes.node.isRequired,
  to: PropTypes.string,
  onClick: PropTypes.func,
  color: PropTypes.shape({
    dark: PropTypes.string,
    main: PropTypes.string,
    light: PropTypes.string,
  }),
  id: PropTypes.string,
};

export default memo(ActionButton, (prevProps, nextProps) => {
  // Return true if passing nextProps would render the same result as prevProps
  // Return false if the render results would be different

  return (
    prevProps.to === nextProps.to && prevProps.onClick === nextProps.onClick
  );
});
